import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, ChevronDown, Gauge, Layers, Zap } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import type { LessonSettings, NamedFlow } from '@/lib/api-client';
import { isFeatureEnabled } from '@/config/app';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

/**
 * FlowSelector - picks one of the three default flow templates plus
 * the user's saved custom flows:
 *   • Quick - mindmap + highlights (cheapest)
 *   • Core  - mindmap + text + highlights (balanced default)
 *   • Full  - every artifact (heaviest)
 *   • <saved flows> - named templates from Settings → Lesson Flows
 *
 * Emits a full `LessonSettings` object so the parent can pass it straight
 * to `lessonApi.generateLesson`. Picking a saved flow sets
 * `flow: 'custom'` with that flow's `custom_steps` and stashes the name
 * in `options.savedFlowName` so the pill label can show the saved name
 * instead of the generic "Custom".
 *
 * Custom flows are managed in Settings - there's no inline checklist here
 * (that ambiguity confused the picker UX, see commit history).
 */

/**
 * Built-in flows. Custom flows aren't an option in the picker anymore -
 * they're created in Settings → Lesson Flows and surface here as named
 * entries (see `savedFlows` prop). Keeps the picker focused on "which
 * lesson shape do I want", not "let me build a flow inline".
 */

interface FlowOption {
  value: 'quick' | 'core' | 'full';
  label: string;
  description: string;
  icon: typeof Zap;
}

// Descriptions list the FULL step set each built-in flow generates,
// mirroring `DEFAULT_FLOWS` in backend/src/services/flow_service.py. Keep
// these in sync - they're the only signal users see before generating.
// While video generation is gated off (VITE_ENABLE_VIDEO), the backend
// strips `video` from the resolved flow, so the "Full" description drops it
// too rather than promising an artifact that won't be generated.
const FULL_DESCRIPTION = isFeatureEnabled('video')
  ? 'Mindmap, text, quiz, video, glossary, highlights'
  : 'Mindmap, text, quiz, glossary, highlights';

/**
 * Option-row surfaces, shared by the built-in list and the saved-flow list so
 * the two cannot drift apart.
 *
 * The light ramp is `--card` (rest) -> `brand-100` (hover) -> `brand-200`
 * (selected), and both non-rest steps changed:
 *
 *   - Selected was `bg-brand-50/70`, which was invisible. In light,
 *     `--brand-50` and `--card` both round to rgb(244,246,255), so 70% of
 *     brand-50 over a card panel painted the panel's own colour - the selected
 *     row was identified by its tick and its text colour alone. `brand-200`
 *     (rgb 211,219,255) is the real fill that lets the tick go away, which is
 *     the whole point of the change. `text-brand-700` on it measures 5.2:1.
 *   - Hover was `bg-muted` (rgb 210,218,243) - as heavy as the selected row
 *     should be, so hovering read like selecting. `brand-100`
 *     (rgb 232,236,255) is markedly lighter and leaves the selected fill as
 *     the strongest thing in the list.
 *
 * Dark is untouched: a translucent `primary/30` fill for selected and
 * `--accent` for hover already read correctly there.
 */
/**
 * Trigger surfaces. `TRIGGER_OPEN_CLASS` is `TRIGGER_HOVER_CLASS` with the
 * `hover:` prefixes stripped, and that is the point: an open picker should
 * look exactly like a hovered one. Open used to step further - `brand-200` in
 * light, `primary/40` in dark - on the reasoning that open is a stronger state
 * than hover, but there is nothing for that extra weight to communicate. The
 * dropdown itself is already on screen saying the picker is open, so the
 * darker trigger just read as a second, louder announcement of the same fact.
 *
 * KEEP THE TWO IN SYNC. They cannot share one string - Tailwind's JIT scans
 * source text, so a computed `'hover:' + cls` produces no utility at all.
 */
const TRIGGER_HOVER_CLASS =
  'hover:border-brand-400 hover:bg-brand-100 hover:text-brand-700 dark:hover:border-primary/60 dark:hover:bg-primary/20 dark:hover:text-primary';
const TRIGGER_OPEN_CLASS =
  'border-brand-400 bg-brand-100 text-brand-700 dark:border-primary/60 dark:tint-brand';

const ROW_CLASS =
  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors';
const ROW_HOVER_CLASS = 'hover:bg-brand-100 dark:hover:bg-accent';
const ROW_ACTIVE_CLASS = 'bg-brand-200 dark:bg-tint-brand';

const FLOWS: readonly FlowOption[] = [
  {
    value: 'quick',
    label: 'Quick',
    description: 'Mindmap + highlights - fastest to skim',
    icon: Zap,
  },
  {
    value: 'core',
    label: 'Core',
    description: 'Mindmap + text + highlights (default)',
    icon: Gauge,
  },
  {
    value: 'full',
    label: 'Full',
    description: FULL_DESCRIPTION,
    icon: Layers,
  },
] as const;

interface FlowSelectorProps {
  value: LessonSettings;
  onChange: (next: LessonSettings) => void;
  disabled?: boolean;
  /**
   * User-saved custom flow templates from `User.settings.saved_custom_flows`.
   * Surface them as named entries below the four defaults so picking
   * "My exam prep" maps to `{ flow: 'custom', custom_steps: [...] }`
   * without needing to rebuild the step list each time.
   */
  savedFlows?: NamedFlow[];
  /**
   * Where the dropdown panel renders relative to the trigger.
   * Format is `<vertical>-<alignment>`. Both options open downward -
   * upward placement got cut off too easily and we ditched it.
   *
   * `'bottom-end'` (default) - right-aligned: panel's right edge sits
   *   under the trigger's right edge and extends leftward. Right for
   *   triggers near the right edge of their container (home prompt box).
   *
   * `'bottom-start'` - left-aligned: panel's left edge sits under the
   *   trigger's left edge and extends rightward. Right for triggers
   *   near the left edge of their container (settings page).
   */
  placement?: 'bottom-end' | 'bottom-start';
  /**
   * Render with the panel already open.
   *
   * The open state is otherwise local and has no prop, which made the entire
   * dropdown — the built-in list, the saved-flow rows, the selection highlight —
   * impossible to render without clicking. That matters beyond the catalog:
   * the saved-flow rows only mount when open, so a malformed `savedFlows` entry
   * threw on a surface nothing ever rendered.
   *
   * Initial state only; the picker still owns `isOpen` after mount, and no
   * production call site passes this.
   */
  defaultOpen?: boolean;
}

export function FlowSelector({
  value,
  onChange,
  disabled = false,
  savedFlows = [],
  placement = 'bottom-end',
  defaultOpen = false,
}: FlowSelectorProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const containerRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  // Below `sm` the options open as a bottom sheet instead of a dropdown: a
  // 300px panel hanging off a pill covered the Learn button and the topics,
  // and a sheet is where a phone user expects a short pick-one list.
  const isPhone = useMediaQuery('(max-width: 639.98px)');

  useEffect(() => {
    if (!isOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // The phone sheet is portalled out of `containerRef`, so a press
      // inside it must not count as "outside".
      if (
        !containerRef.current?.contains(target) &&
        !sheetRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  // Detect which saved flow (if any) is currently active. We stash the
  // chosen flow's name in `options.savedFlowName` when the user picks one
  // so the pill label can show "My exam prep" instead of generic "Custom".
  const activeSavedFlowName =
    value.flow === 'custom'
      ? (value.options?.savedFlowName as string | undefined)
      : undefined;
  const activeSavedFlow = activeSavedFlowName
    ? savedFlows.find(f => f.name === activeSavedFlowName)
    : undefined;

  // Pill label resolution:
  //  1. Saved flow active        → "My exam prep"
  //  2. Built-in flow active     → "Quick" / "Core" / "Full"
  //  3. flow='custom' but the saved-flow ref is stale (deleted, or
  //     account-default points at a flow this user no longer has) →
  //     show generic "Custom" so we don't lie about which flow is active
  const builtIn = FLOWS.find(f => f.value === value.flow);
  const ActiveIcon = activeSavedFlow ? Bookmark : (builtIn?.icon ?? Bookmark);
  const activeLabel = activeSavedFlow
    ? activeSavedFlow.name
    : (builtIn?.label ?? 'Custom');

  const setBuiltInFlow = (flow: FlowOption['value']) => {
    // Drop any saved-flow name when explicitly switching to a built-in.
    const { savedFlowName: _drop, ...restOptions } = value.options || {};
    onChange({
      ...value,
      flow,
      custom_steps: undefined,
      options: restOptions,
    });
    setIsOpen(false);
  };

  const pickSavedFlow = (saved: NamedFlow) => {
    onChange({
      ...value,
      flow: 'custom',
      custom_steps: [...saved.steps],
      options: { ...(value.options || {}), savedFlowName: saved.name },
    });
    setIsOpen(false);
  };

  const options = (
    <FlowOptionList
      value={value}
      savedFlows={savedFlows}
      activeSavedFlowName={activeSavedFlowName}
      hasActiveSavedFlow={Boolean(activeSavedFlow)}
      onBuiltIn={setBuiltInFlow}
      onSaved={pickSavedFlow}
      roomy={isPhone}
    />
  );

  return (
    <div ref={containerRef} className='relative inline-block'>
      <button
        type='button'
        onClick={() => !disabled && setIsOpen(o => !o)}
        disabled={disabled}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        // Named explicitly: below `sm` the visible "Flow" label is hidden,
        // and the name fell back to the bare value ("Standard").
        aria-label={`Flow ${activeLabel}`}
        className={cn(
          // `tap-target`: drawn 38px tall, tapped as 44 on a phone.
          'tap-target group flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200',
          'border-border bg-card text-foreground shadow-sm',
          // `brand-100`/`brand-400`, not `brand-50`/`brand-200`. In light,
          // `--brand-50` and `--card` both round to rgb(244,246,255), so the
          // old hover fill repainted the button its own colour and only the
          // text moved - while dark got a real `primary/20` wash, which is why
          // this read as broken in light only. `brand-200` on the border was
          // the same class of bug: lighter than the resting `--border`, so the
          // edge went softer on hover. Matched to the Attach button beside it,
          // which had drifted to a third combination.
          TRIGGER_HOVER_CLASS,
          'focus:outline-none focus:!ring-0 focus:!ring-offset-0 focus-visible:!ring-0 focus-visible:!ring-offset-0',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isOpen && TRIGGER_OPEN_CLASS
        )}
      >
        <ActiveIcon
          className={cn(
            'h-3.5 w-3.5 text-brand-600 transition-colors dark:text-primary',
            disabled && 'text-muted-foreground'
          )}
        />
        <span className='flex items-baseline gap-1.5'>
          {/* `text-label` (12px), not the old `text-xs`: the repo's type
              floor is 12px (tailwind.config.js), and 10px caps were the
              smallest text on the home screen. Hidden on a phone, where the
              icon and the value carry it and the composer's one-row toolbar
              needs the width. */}
          <span className='hidden text-label font-semibold uppercase tracking-wider text-muted-foreground sm:inline'>
            Flow
          </span>
          <span className='max-w-[140px] truncate'>{activeLabel}</span>
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
            isOpen ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>

      {isOpen && !isPhone && (
        <div
          className={cn(
            'absolute top-full z-50 mt-2 w-[300px] rounded-2xl border shadow-lg',
            placement === 'bottom-start' ? 'left-0' : 'right-0',
            'border-border bg-card'
          )}
        >
          {options}
        </div>
      )}

      {isOpen &&
        isPhone &&
        createPortal(
          <FlowSheet sheetRef={sheetRef} onClose={() => setIsOpen(false)}>
            {options}
          </FlowSheet>,
          document.body
        )}
    </div>
  );
}

/**
 * The phone presentation: a sheet from the bottom edge over a light scrim.
 *
 * Portalled to `document.body`, not rendered in place - the home composer is
 * `backdrop-blur`, and a `position: fixed` element inside a backdrop-filtered
 * ancestor is fixed to THAT box, not the viewport, so an in-place sheet
 * opened inside the composer. Same listbox, same rows as the desktop panel.
 * A scrim tap or Escape closes it (Escape via the selector's own listener).
 */
function FlowSheet({
  sheetRef,
  onClose,
  children,
}: {
  sheetRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className='fixed inset-0 z-50 flex items-end'>
      <div
        aria-hidden
        onClick={onClose}
        className='absolute inset-0 bg-black/30 duration-200 animate-in fade-in'
      />
      {/* A modal dialog: the scrim takes every tap outside it, so to a
          screen reader (and the phone checks) it is the whole screen. */}
      <div
        ref={sheetRef}
        role='dialog'
        aria-modal='true'
        aria-label='Lesson flow'
        className='relative w-full rounded-t-2xl border-t border-border bg-card px-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 shadow-2xl duration-300 animate-in slide-in-from-bottom'
      >
        {/* The grabber is the sheet's affordance, not a control. */}
        <div
          aria-hidden
          className='mx-auto mb-2 h-1 w-10 rounded-full bg-muted-foreground/30'
        />
        <p className='px-3 pb-1 text-subhead text-foreground'>Lesson flow</p>
        {children}
      </div>
    </div>
  );
}

/** The built-in flows, then any saved flows, as one labelled listbox. */
function FlowOptionList({
  value,
  savedFlows,
  activeSavedFlowName,
  hasActiveSavedFlow,
  onBuiltIn,
  onSaved,
  roomy,
}: {
  value: LessonSettings;
  savedFlows: NamedFlow[];
  activeSavedFlowName: string | undefined;
  hasActiveSavedFlow: boolean;
  onBuiltIn: (flow: FlowOption['value']) => void;
  onSaved: (saved: NamedFlow) => void;
  /** Phone rows: taller, so each is a comfortable thumb target. */
  roomy: boolean;
}) {
  // `min-h-14` on the phone sheet: 56px rows, one per thumb.
  const rowClass = cn(ROW_CLASS, roomy && 'min-h-14 py-3');
  return (
    <div
      role='listbox'
      // A `role=listbox` needs its own accessible name - it had none, so
      // axe's `aria-input-field-name` failed and a screen reader announced
      // an unnamed list of five options. The trigger's `aria-haspopup`
      // does not supply one. Surfaced by the play function on
      // LearningPrompt's `WithSavedFlows` story, which is the first thing
      // that ever opened this panel under an axe scan.
      aria-label='Lesson flow'
      className='space-y-1 p-1.5'
    >
      {FLOWS.map(option => {
        // Built-ins highlight only when no saved flow is active - a
        // saved-flow pick lives in flow=custom space and we don't want both a
        // built-in and a saved flow looking selected at once.
        const isActive = option.value === value.flow && !hasActiveSavedFlow;
        return (
          <FlowRow
            key={option.value}
            className={rowClass}
            isActive={isActive}
            icon={option.icon}
            title={option.label}
            detail={option.description}
            onSelect={() => onBuiltIn(option.value)}
          />
        );
      })}

      {savedFlows.length > 0 && (
        <>
          <div className='my-1 border-t border-border' />
          {savedFlows.map(saved => (
            <FlowRow
              key={saved.name}
              className={rowClass}
              isActive={activeSavedFlowName === saved.name}
              icon={Bookmark}
              title={saved.name}
              detail={saved.steps?.join(' · ') || '(no steps)'}
              onSelect={() => onSaved(saved)}
              truncate
            />
          ))}
        </>
      )}
    </div>
  );
}

function FlowRow({
  className,
  isActive,
  icon: Icon,
  title,
  detail,
  onSelect,
  truncate = false,
}: {
  className: string;
  isActive: boolean;
  icon: FlowOption['icon'];
  title: string;
  detail: string;
  onSelect: () => void;
  truncate?: boolean;
}) {
  return (
    <button
      role='option'
      aria-selected={isActive}
      onClick={onSelect}
      className={cn(
        className,
        // Hover only applies to non-active rows so hovering the
        // already-selected flow doesn't wipe its highlight.
        !isActive && ROW_HOVER_CLASS,
        isActive && ROW_ACTIVE_CLASS
      )}
    >
      <span
        className={cn(
          'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full',
          // The circle used to be `bg-muted` unconditionally, which worked
          // while the active row had no visible fill in light. Now that it
          // does, light `--muted` (rgb 210,218,243) and the active row's
          // `brand-200` (rgb 211,219,255) are the same lightness and the
          // circle dissolves into the row - so on an active row in LIGHT it
          // flips to `bg-card`, reading as a pale disc on a tinted row. Dark
          // keeps `bg-muted` throughout, since its active fill is a
          // translucent wash that the grey disc still separates from.
          isActive
            ? 'bg-card text-brand-600 dark:bg-muted dark:text-primary'
            : 'bg-muted text-muted-foreground'
        )}
      >
        <Icon className='h-3.5 w-3.5' />
      </span>
      <div className='min-w-0 flex-1'>
        <div
          className={cn(
            'text-sm font-semibold',
            truncate && 'truncate',
            isActive
              ? 'text-brand-700 dark:text-tint-brand-foreground'
              : 'text-foreground'
          )}
        >
          {title}
        </div>
        <div
          className={cn(
            'text-xs text-muted-foreground',
            truncate && 'truncate'
          )}
        >
          {detail}
        </div>
      </div>
    </button>
  );
}
